"use client";
import { motion } from "framer-motion";
import { FiMail, FiMapPin, FiPhone, FiSend } from "react-icons/fi";
import InputWithIcon from "./Inputs";
import { InfoIcon, Mail, User } from "lucide-react";
import { useState } from "react";
import { FaEnvelope } from "react-icons/fa";
import { useLanguage } from "@/context/LanguageContext";

import { toast } from "sonner";

export default function ContactSection() {
  const { t } = useLanguage();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, subject, message }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Une erreur est survenue');
      }

      toast.success('Message envoyé avec succès ! Je vous répondrai dès que possible.');
      // Reset form
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message. Veuillez réessayer.");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  return (
    <section className="section-atmosphere section-atmosphere-cool py-20 px-6 md:px-12 lg:px-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-14 grid gap-6 text-left lg:grid-cols-[0.85fr_1.15fr] lg:items-end"
        >
          <motion.span
            variants={itemVariants}
            className="inline-block py-1 px-3 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4"
          >
            {t.contact.badge}
          </motion.span>
          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-6xl font-bold tracking-tight text-foreground"
          >
            {t.contact.title}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed"
          >
            {t.contact.subtitle}
          </motion.p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
            className="lg:w-5/12 space-y-8"
          >
            <div className="relative overflow-hidden rounded-[2rem] border border-foreground/10 bg-foreground/[0.04] p-8 shadow-2xl shadow-black/5 backdrop-blur-xl">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
                {t.common.coordinates}
              </h3>

              <div className="space-y-8">
                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-start gap-5 group"
                >
                  <div className="p-4 bg-primary/10 dark:bg-primary/20 rounded-2xl text-primary group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <FiMail className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">
                      {t.contact.info.email}
                    </h4>
                    <a
                      href="mailto:emmanueladolphe0401@gmail.com"
                      className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors block break-all"
                    >
                      emmanueladolphe0401@gmail.com
                    </a>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-start gap-5 group"
                >
                  <div className="p-4 bg-primary/10 dark:bg-primary/20 rounded-2xl text-primary group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <FiMapPin className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">
                      {t.contact.info.location}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-300 break-words">
                      Fianarantsoa, Madagascar
                    </p>
                    <span className="text-sm text-green-600 dark:text-green-400 font-medium mt-1 inline-block">
                      • {t.contact.info.availableRemote}
                    </span>
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-start gap-5 group"
                >
                  <div className="p-4 bg-primary/10 dark:bg-primary/20 rounded-2xl text-primary group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <FiPhone className="w-6 h-6" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-1">
                      {t.contact.info.phone}
                    </h4>
                    <a
                      href="tel:+261340854816"
                      className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors block break-all"
                    >
                      +261 34 08 548 16
                    </a>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
            viewport={{ once: true }}
            className="lg:w-7/12"
          >
            <div className="rounded-[2rem] border border-foreground/10 bg-background/55 p-6 shadow-2xl shadow-black/5 backdrop-blur-xl md:p-10">
              <form onSubmit={onSubmit} className="space-y-6">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <InputWithIcon
                    icon={User}
                    type="text"
                    placeholder={t.contact.form.namePlaceholder}
                    onChange={(value) => setName(value)}
                    label={t.contact.form.name}
                    value={name}
                  />
                  <InputWithIcon
                    label={t.contact.form.email}
                    onChange={(value) => setEmail(value)}
                    value={email}
                    type="email"
                    icon={Mail}
                    placeholder={t.contact.form.emailPlaceholder}
                  />
                </div>

                <InputWithIcon
                  label={t.contact.form.subject}
                  value={subject}
                  onChange={(value) => setSubject(value)}
                  type="text"
                  icon={InfoIcon}
                  placeholder={t.contact.form.subjectPlaceholder}
                />

                <InputWithIcon
                  label={t.contact.form.message}
                  value={message}
                  onChange={(value) => setMessage(value)}
                  type="textarea"
                  icon={FaEnvelope}
                  placeholder={t.contact.form.messagePlaceholder}
                />

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02, boxShadow: "0 10px 30px -10px rgba(152, 89, 55, 0.3)" }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full bg-primary text-white font-bold py-4 px-8 rounded-xl transition-all flex items-center justify-center gap-3 group ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''} hover:bg-primary/90`}
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>{t.contact.form.send}</span>
                      <FiSend className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </div >
    </section >
  );
}
